import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "@/lib/content";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-100"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex h-[72px] items-center justify-between">
          <Link to="/" data-testid="nav-home-logo" aria-label="Bright EOR home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label="Primary">
            {NAV.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-[14px] font-medium tracking-tight transition-colors ${
                    active ? "text-navy" : "text-slateink hover:text-aqua"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="header-cta-book-consultation"
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-[14px] font-medium text-white transition-all hover:bg-aqua hover:text-navy hover:shadow-aqua-glow"
            >
              Book a Consultation
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          <button
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-navy"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((s) => !s)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="mx-auto max-w-7xl px-6 py-6 space-y-1">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block rounded-lg px-3 py-3 text-base font-medium text-navy hover:bg-navy-50"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-header-cta"
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-medium text-white"
            >
              Book a Consultation
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
